import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Keeps exactly one free gift in the cart while the basket is at tier 4, and none
 * below it. Mounted by snippets/cart-free-gift.liquid, which explains the discount
 * that actually makes the gift €0.
 *
 * Every decision is made from `/cart.js`, never from the event that triggered it,
 * and every write is by line KEY rather than line number — `cart-addons.js` reacts to
 * the same events and may remove a line in between, which shifts every line number
 * after it.
 *
 * The one rule that protects the customer: a gift line that is not €0 after the
 * cart settles is removed, and the element stops adding it for a while. That covers
 * the discount not existing yet, being paused, or having its conditions edited so
 * they no longer match this threshold.
 *
 * "A while" is UNPRICED_BACKOFF_MS, not the whole session. The first version held
 * the flag for the session, and a tab that had loaded the cart in the minutes
 * between the setting being switched on and the discount being activated never
 * offered the gift again — €160 in the cart, "All rewards unlocked", no gift.
 */
const UNPRICED_FLAG = 'cart-free-gift:unpriced:';
const UNPRICED_BACKOFF_MS = 10 * 60 * 1000;

class CartFreeGift extends HTMLElement {
  #running = false;

  connectedCallback() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onUpdate);
    // The server already worked out whether this cart is out of step; the common
    // case — nothing to do — costs no request.
    if (this.dataset.needsAction === 'true') this.#run();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onUpdate);
  }

  get #variantId() {
    return Number(this.dataset.variantId);
  }

  get #productId() {
    return Number(this.dataset.productId);
  }

  get #threshold() {
    return Number(this.dataset.threshold);
  }

  /** @param {Event & {promise?: Promise<unknown>}} event */
  #onUpdate = (event) => {
    if (event.target === this) return; // our own re-render announcement
    // Same race as cart-addons.js: wait for the change to commit, but never forever.
    const committed = Promise.resolve(event.promise).catch(() => {});
    const deadline = new Promise((resolve) => setTimeout(resolve, 1500));
    Promise.race([committed, deadline]).then(() => this.#run());
  };

  #run() {
    if (this.#running) return;
    if (!this.#variantId || !this.#threshold) return;
    this.#running = true;
    this.#reconcile()
      .then((changed) => changed && this.#announce())
      .catch((error) => console.warn('[cart-free-gift] skipped:', error))
      .finally(() => {
        this.#running = false;
      });
  }

  /** @returns {Promise<boolean>} whether the cart was changed */
  async #reconcile() {
    let cart = await this.#cart();
    let { gifts, eligible } = this.#read(cart);
    let changed = false;

    if (eligible < this.#threshold) {
      for (const line of gifts) {
        await this.#change(line.key, 0);
        changed = true;
      }
      return changed;
    }

    if (gifts.length === 0) {
      if (this.#unpriced) return false;
      await this.#post(Theme.routes.cart_add_url, {
        items: [{ id: this.#variantId, quantity: 1, properties: { _free_gift: '1' } }],
      });
      changed = true;
      cart = await this.#cart();
      ({ gifts } = this.#read(cart));
    }

    // One gift, not one per click on "+" or one per tab that raced to add it.
    const [first, ...extra] = gifts;
    for (const line of extra) {
      await this.#change(line.key, 0);
      changed = true;
    }
    if (first && first.quantity > 1) {
      await this.#change(first.key, 1);
      changed = true;
      cart = await this.#cart();
      ({ gifts } = this.#read(cart));
    }

    const gift = gifts[0];
    if (gift && gift.final_line_price > 0) {
      await this.#change(gift.key, 0);
      this.#unpriced = true;
      console.warn(
        '[cart-free-gift] the gift was not free after adding it, so it was removed. ' +
          'Check that the Buy X get Y discount exists, is active, and requires the same amount as the cart bar.'
      );
      changed = true;
    }
    return changed;
  }

  /** @param {any} cart */
  #read(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const gifts = items.filter((item) => item.product_id === this.#productId);
    const giftTotal = gifts.reduce((sum, item) => sum + (item.final_line_price || 0), 0);
    return { gifts, eligible: (cart?.items_subtotal_price || 0) - giftTotal };
  }

  /** True while a recent attempt found the gift not free. Stores when, not whether. */
  get #unpriced() {
    try {
      const since = Number(sessionStorage.getItem(UNPRICED_FLAG + this.#variantId));
      return since > 0 && Date.now() - since < UNPRICED_BACKOFF_MS;
    } catch {
      return false;
    }
  }

  set #unpriced(value) {
    try {
      sessionStorage.setItem(UNPRICED_FLAG + this.#variantId, value ? String(Date.now()) : '0');
    } catch {
      // Private mode: the next page view may try once more, and remove it again.
    }
  }

  /** @param {string} key @param {number} quantity */
  #change(key, quantity) {
    return this.#post(Theme.routes.cart_change_url, { id: key, quantity });
  }

  /**
   * `fetchConfig('json')`, not `'javascript'` — the latter drops Content-Type and
   * Shopify answers 400 with an empty body. See cart-addons.js.
   *
   * @param {string} url
   * @param {object} body
   */
  async #post(url, body) {
    const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(body) }));
    const text = await response.text();
    /** @type {any} */
    let parsed = {};
    try {
      parsed = text ? JSON.parse(text) : {};
    } catch {
      throw new Error(`${url} returned ${response.status} with an unparseable body`);
    }
    if (!response.ok || parsed.status) {
      throw new Error(parsed.description || parsed.message || `${url} returned ${response.status}`);
    }
    return parsed;
  }

  async #cart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    const text = await response.text();
    if (!response.ok || !text) throw new Error(`cart.js returned ${response.status}`);
    return JSON.parse(text);
  }

  /** Re-render the cart from one payload, exactly as cart-addons.js does. */
  async #announce() {
    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

    const url = new URL(`${Theme.routes.cart_url}.js`, window.location.origin);
    if (sections.length) url.searchParams.set('sections', sections.join(','));
    const cart = await fetch(url, { headers: { Accept: 'application/json' } }).then((res) => res.json());

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({ action: 'add', context: 'cart', lines: [], promise: deferred.promise })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'cart-free-gift',
        sourceId: 'cart-free-gift',
        itemCount: cart.item_count,
        sections: cart.sections,
        didError: false,
      },
    });
  }
}

if (!customElements.get('cart-free-gift')) {
  customElements.define('cart-free-gift', CartFreeGift);
}
